/**
 * @file Renders the user's profile page, with its mobile number, two-factor authentication and personal access tokens.
 */

import React, { useRef } from "react";
import { Descriptions, Typography } from "antd";
import { useAppContext } from "@components/AppContext";
import { Value } from "@components/Text";
import { W0, W1 } from "@components/Constants";
import PersonalAccessTokensTable from "@components/PersonalAccessTokens";
import { TwoFactorAuthenticationSwitch } from "@components/Identity";
import {
  disableMyUser2FAOperation,
  enableMyUser2FAOperation,
} from "@api/services/backstage";
import { UserMobileUpdateModal, type UserMobileUpdateModalHandle } from "@components/Users";
import { type AppContext } from "@components/AppContext/types";
import { isPersonalAccessTokensEnabled } from "@components/Organization";

const LABEL_TITLE = "User Profile";
const LABEL_NAME = "Name";
const LABEL_EMAIL = "Email";
const LABEL_MOBILE = "Mobile";
const LABEL_ADD_NUMBER = "Add Mobile";
const LABEL_TWO_FACTOR_AUTHENTICATION = "Two Factor Authentication";
const LABEL_EMPTY = "-";

type Props = {
  userId: string;
  name?: string;
  email?: string;
  mobile?: string;
  is2FaEnabled?: boolean;
  onSuccessUpdateMobile: (mobile: string) => void;
  onSuccessSwitch2Fa: (shouldEnable: boolean) => Promise<void> | void;
};

/**
 * Returns the value, or a placeholder dash when it is empty.
 */
const getTextValue = (value?: string) => {
  const hasValue = !!value;

  if (!hasValue) {
    return LABEL_EMPTY;
  }

  return value;
};

/**
 * Renders the user's profile, including name, email, mobile, two-factor authentication and personal access tokens.
 */
const MyProfile = ({
  userId,
  name,
  email,
  mobile,
  is2FaEnabled,
  onSuccessUpdateMobile,
  onSuccessSwitch2Fa,
}: Props) => {
  const {
    isRoot,
    hasConsoleAccess,
    getOrganizationId,
    isConsoleOrganization,
  } = useAppContext() as AppContext;

  const userMobileUpdateModal = useRef<
    UserMobileUpdateModalHandle | Record<string, never>
  >({});

  const hasMobileNumber = Boolean(mobile);
  const mobileLabel = hasMobileNumber ? mobile : LABEL_ADD_NUMBER;

  const shouldHideSecurityFields =
    hasConsoleAccess &&
    !isConsoleOrganization;

  // NOTE: personal access tokens have their own list of organizations, and
  //       the external investor portal has a separate one.
  const isEnabledForOrganization = isPersonalAccessTokensEnabled(getOrganizationId());

  // NOTE: a platform administrator reaches each organization, therefore that
  //       person must not hold a credential that a second factor never
  //       protects. CreateMyPersonalAccessToken refuses such a caller with a
  //       403, and isRoot covers an administrator of the console organization
  //       and an address of the roots config of api-identity.
  const isPlatformAdministrator = isRoot === true;

  // NOTE: a console operator that opens a different organization acts as
  //       the user of a console user group, and the profile card shows the
  //       name and the address of that group. The tokens belong to the
  //       operator, therefore the table stays on the console organization,
  //       as the security fields do.
  const shouldShowPersonalAccessTokens =
    isEnabledForOrganization &&
    !isPlatformAdministrator &&
    !shouldHideSecurityFields;

  // NOTE: the profile description list stays narrow, the tokens table needs
  //       the full width of the page for its columns.
  const profileStyle = {
    maxWidth: 650,
  };

  const personalAccessTokensStyle = {
    marginTop: W1 + W0,
  };

  /**
   * Opens the modal that updates the user's mobile number.
   */
  const openUserMobileUpdateModal = () =>
    userMobileUpdateModal.current?.open(userId);

  /**
   * Applies the updated mobile number returned by the mobile update modal.
   */
  const onMobileUpdateSuccess = (updatedUser: { mobile?: string }) => {
    const mobileNumber = updatedUser?.mobile || "";
    onSuccessUpdateMobile(mobileNumber);
  };

  return (
    <>
      <div style={profileStyle}>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          {LABEL_TITLE}
        </Typography.Title>

        <Descriptions
          bordered
          size="small"
          column={1}
          labelStyle={{ width: 220 }}
        >
          <Descriptions.Item label={LABEL_NAME}>
            {getTextValue(name)}
          </Descriptions.Item>

          <Descriptions.Item label={LABEL_EMAIL}>
            {getTextValue(email)}
          </Descriptions.Item>

          {
            !shouldHideSecurityFields && (
              <>
                <Descriptions.Item label={LABEL_MOBILE}>
                  <Value onClick={openUserMobileUpdateModal} value={mobileLabel} />
                </Descriptions.Item>

                <Descriptions.Item label={LABEL_TWO_FACTOR_AUTHENTICATION}>
                  <TwoFactorAuthenticationSwitch
                    mobileNumber={mobile}
                    isEnabled={is2FaEnabled === true}
                    isDisabled={!hasMobileNumber}
                    enableOperation={enableMyUser2FAOperation}
                    disableMyOperation={disableMyUser2FAOperation}
                    verificationEnableOperation={enableMyUser2FAOperation}
                    onSuccess={onSuccessSwitch2Fa}
                  />
                </Descriptions.Item>
              </>
            )
          }
        </Descriptions>
      </div>

      {shouldShowPersonalAccessTokens && (
        <div style={personalAccessTokensStyle}>
          <PersonalAccessTokensTable />
        </div>
      )}

      <UserMobileUpdateModal
        modal={userMobileUpdateModal}
        onSuccess={onMobileUpdateSuccess}
      />
    </>
  );
};

export default MyProfile;
